import { type AccordionItemProps } from "@mantine/core";
import { Accordion, Box, BoxProps, ActionIcon, Title, Group } from "@mantine/core";
import { StyledMarkdown } from "./StyledMarkdown";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

interface StyledAccordionItemProps extends AccordionItemProps {
  children: React.ReactNode;
  // explicit CSS props
  bg?: React.CSSProperties["backgroundColor"];
  backgroundImage?: React.CSSProperties["backgroundImage"];
  backgroundSize?: React.CSSProperties["backgroundSize"];
  backgroundRepeat?: React.CSSProperties["backgroundRepeat"];
  backgroundPosition?: React.CSSProperties["backgroundPosition"];
}

export type StyledAccordionTriggerIconFn = (isExpanded: boolean) => React.ReactNode;

interface StyledAccordionTriggerProps extends BoxProps {
  children?: React.ReactNode;
  isExpanded: boolean;
  title: string;
  description: string;
  hideCloseButton?: boolean;
  iconFn?: StyledAccordionTriggerIconFn;
  textColor?: string;
  backgroundColor?: React.CSSProperties["backgroundColor"];
}

interface StyledAccordionItemContentProps extends BoxProps {
  children: React.ReactNode;
  bg?: React.CSSProperties["backgroundColor"];
  isExpanded?: boolean;
}

function StyledAccordionItem({ children, ...props }: StyledAccordionItemProps) {
  const cssRadius = "var(--mantine-radius-md)";
  const { bg, backgroundImage, backgroundSize, backgroundRepeat, backgroundPosition, ...rest } = props;

  return (
    <Accordion.Item
      {...rest}
      style={{
        border: "none",
        borderRadius: cssRadius,
        marginBottom: 0, // Remove marginBottom for grid layout
        backgroundColor: bg,
        backgroundImage,
        backgroundSize,
        backgroundRepeat,
        backgroundPosition,
        overflow: "hidden", // needed for radius
      }}
    >
      {children}
    </Accordion.Item>
  );
}

export { StyledAccordionItem };

function StyledAccordionTrigger({
  isExpanded,
  title,
  description,
  hideCloseButton,
  iconFn,
  textColor = "white",
  backgroundColor,
  ...props
}: StyledAccordionTriggerProps) {
  const content = (
    <Box
      style={{
        flex: 1,
        textAlign: "left",
        color: "var(--mantine-color-white)",
      }}
    >
      <Group align="center" mb="xs" mt="xs">
        {iconFn?.(isExpanded)}
        <Title size="h3" c={textColor} style={{ margin: 0 }}>
          {title}
        </Title>
      </Group>
      <StyledMarkdown content={description} />
    </Box>
  );

  return (
    <Accordion.Control
      {...props}
      mod={{ hideCloseButton }}
      style={{
        ...props.style,
        backgroundColor: backgroundColor ?? "transparent",
        cursor: hideCloseButton ? "default" : "pointer",
        paddingTop: "var(--mantine-spacing-xs)",
        paddingBottom: "var(--mantine-spacing-xs)",
        paddingLeft: "var(--mantine-spacing-md)",
        paddingRight: "var(--mantine-spacing-md)",
        transition: "background-color 0.2s ease",
      }}
      chevron={
        hideCloseButton ? (
          <Box style={{ width: 0, height: 0 }} />
        ) : (
          <ActionIcon variant="transparent" c={textColor}>
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </ActionIcon>
        )
      }
    >
      {content}
    </Accordion.Control>
  );
}

export { StyledAccordionTrigger };

function StyledAccordionItemContent({ children, ...props }: StyledAccordionItemContentProps) {
  const { bg, ...rest } = props;
  const cssRadius = "var(--mantine-radius-md)";

  return (
    <Accordion.Panel
      {...rest}
      style={{
        backgroundColor: bg,
        borderBottomLeftRadius: cssRadius,
        borderBottomRightRadius: cssRadius,
        padding: 0,
      }}
    >
      {children}
    </Accordion.Panel>
  );
}
export { StyledAccordionItemContent };
